/**
 * ★ THE PERIOD CHIPS' ARITHMETIC - "Last 7 days / Last 30 days / This month", and the period before
 * each, which the Overview and Statistics compare against (design 15, decision 2: the owner agreed on
 * 2026-10-01 to the prototype's range chips and its "+3% compared with the previous 30 days").
 * ★★ ONE PERIOD BUTTON SINCE DESIGN 24 (T15; the owner, 2026-10-11, option 1): the chips became the
 * set periods of one menu - those three, Last 90 days, Last month, Year to date, Last 12 months and
 * Last year - and a custom range, checked here against the longest range the server reads. Every
 * screen hands `@moonx/ui`'s `PeriodPicker` the props of `periodButtonFor`, so all offer the same.
 *
 * NO CLOCK. This app has none and must not have one (./format.ts says why: C11 and D9). Every period
 * here is counted back from a day the SERVER named - `Dashboard.to`, "the most recent completed day"
 * on its own clock (src/modules/analytics/cabinet/analytics-window.ts) - which a screen learns from
 * its first `getDashboard` read, made with no range. What this file does is arithmetic on that
 * `YYYY-MM-DD` string: a parse of a value the server sent, which C11 exempts, and `Date.UTC`, which
 * reads no clock. So `CLOCK_MODE=test` moves these periods exactly as it moves everything else.
 *
 * A period is INCLUSIVE at both ends, as `getDashboard`'s and `listArticleStats`'s `from` and `to`
 * are (analytics-window.ts, "A WINDOW OF `N` DAYS ENDING ON `to` IS `[to - (N-1), to]`"). The period
 * before is the same number of days, ending the day before.
 */
import type { PeriodPickerProps } from '@moonx/ui';
import { i18n } from '../../i18n';
import type { StatDelta } from './components/kit';
import { formatDaySpan } from './format';
import { liveTexts } from './live-texts';

/**
 * The set periods. No "All time": the server reads at most 366 days, and every view ever is its own
 * figure ("All-time views").
 */
export type PeriodKind =
  | 'last7'
  | 'last30'
  | 'last90'
  | 'thisMonth'
  | 'lastMonth'
  | 'yearToDate'
  | 'last12Months'
  | 'lastYear';

/** The set periods in the menu's order (design 24, T15). */
export const PERIOD_KINDS: readonly PeriodKind[] = [
  'last7',
  'last30',
  'last90',
  'thisMonth',
  'lastMonth',
  'yearToDate',
  'last12Months',
  'lastYear',
];

/** Each set period's name, in the cabinet's language when it is read. */
export const PERIOD_LABELS: Readonly<Record<PeriodKind, string>> = liveTexts(PERIOD_KINDS, (kind) =>
  i18n.t(`periods.${kind}`, { ns: 'cabinet' }),
);

/**
 * The longest range a read answers, both ends counted: "A range longer than 366 days is refused
 * (400)" (authors-api.yaml, `getDashboardDays`) - a year and its leap day.
 */
export const LONGEST_RANGE_DAYS = 366;

/** Two UTC days, `YYYY-MM-DD`, both inclusive - what `from` and `to` take on the wire. */
export interface DayRange {
  readonly from: string;
  readonly to: string;
}

const DAY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const DAY_MS = 86_400_000;

function epochDay(day: string): number {
  const matched = DAY_PATTERN.exec(day);
  if (matched === null) {
    throw new Error(`${day} is not a YYYY-MM-DD day.`);
  }
  return (
    Date.UTC(
      Number.parseInt(matched[1] ?? '', 10),
      Number.parseInt(matched[2] ?? '', 10) - 1,
      Number.parseInt(matched[3] ?? '', 10),
    ) / DAY_MS
  );
}

function dayOf(epoch: number): string {
  return new Date(epoch * DAY_MS).toISOString().slice(0, 10);
}

/** Whether a value is a day this file can count from. A screen guards its server's `to` with it. */
export function isDay(value: string | null | undefined): value is string {
  return typeof value === 'string' && DAY_PATTERN.test(value);
}

/** How many days a period holds, both ends counted. */
export function daysIn(range: DayRange): number {
  return epochDay(range.to) - epochDay(range.from) + 1;
}

/** A set period, ending on `to` - the server's latest complete day - or the calendar's before. */
export function periodEnding(to: string, kind: PeriodKind): DayRange {
  const end = epochDay(to);
  const year = Number.parseInt(to.slice(0, 4), 10);
  switch (kind) {
    case 'last7':
      return { from: dayOf(end - 6), to };
    case 'last30':
      return { from: dayOf(end - 29), to };
    case 'last90':
      return { from: dayOf(end - 89), to };
    case 'lastMonth': {
      // The whole calendar month before `to`'s: its last day is the day before `to`'s month began.
      const last = dayOf(epochDay(`${to.slice(0, 8)}01`) - 1);
      return { from: `${last.slice(0, 8)}01`, to: last };
    }
    case 'yearToDate':
      return { from: `${String(year)}-01-01`, to };
    case 'last12Months': {
      // The day after the same day a year before; a 29 February counts from the 28th's.
      const sameDay = `${String(year - 1)}${to.slice(4) === '-02-29' ? '-02-28' : to.slice(4)}`;
      return { from: dayOf(epochDay(sameDay) + 1), to };
    }
    case 'lastYear':
      return { from: `${String(year - 1)}-01-01`, to: `${String(year - 1)}-12-31` };
    case 'thisMonth':
    default:
      return { from: `${to.slice(0, 8)}01`, to };
  }
}

/** A set period or a custom range: what the period button holds. */
export type PeriodChoice =
  { readonly kind: PeriodKind } | { readonly kind: 'custom'; readonly range: DayRange };

/** The days a choice covers: a set period counted back from `latest`, a custom range as it is. */
export function rangeOf(choice: PeriodChoice, latest: string): DayRange {
  return choice.kind === 'custom' ? choice.range : periodEnding(latest, choice.kind);
}

/** The menu's set periods, each with its days counted back from `latest`. */
export function periodPresets(
  latest: string,
): readonly { readonly value: PeriodKind; readonly label: string; readonly range: DayRange }[] {
  return PERIOD_KINDS.map((kind) => ({
    value: kind,
    label: PERIOD_LABELS[kind],
    range: periodEnding(latest, kind),
  }));
}

/**
 * What a press in the period button's menu chose: a set period, or a custom range the server can
 * read (`customRange`) - `undefined` for one it cannot.
 */
export function choiceOf(
  value: PeriodKind | 'custom',
  range: DayRange,
  latest: string,
): PeriodChoice | undefined {
  if (value !== 'custom') {
    return { kind: value };
  }
  const days = customRange(range.from, range.to, latest);
  return days === undefined ? undefined : { kind: 'custom', range: days };
}

/** What the period button takes, but for its name. */
export type PeriodButtonProps<T extends string = PeriodKind> = Omit<PeriodPickerProps<T>, 'label'>;

/**
 * The period button for a choice: the set periods counted back from `latest`, the chosen one and its
 * days, the longest range, the cabinet's words for a range. A press is handed to `choose` as a
 * choice; a custom range the server cannot read is never handed on.
 */
export function periodButtonFor(
  choice: PeriodChoice,
  latest: string,
  choose: (next: PeriodChoice) => void,
): PeriodButtonProps {
  return {
    presets: periodPresets(latest),
    value: choice.kind,
    range: rangeOf(choice, latest),
    latest,
    longest: LONGEST_RANGE_DAYS,
    formatRange: formatDaySpan,
    onChange: (value, days) => {
      const next = choiceOf(value, days, latest);
      if (next !== undefined) {
        choose(next);
      }
    },
  };
}

/**
 * A custom range from two picked days, in either order - or `undefined` when it cannot be read: not
 * two days, ending after `latest` (no day the server has not counted), or longer than
 * `LONGEST_RANGE_DAYS`.
 */
export function customRange(first: string, second: string, latest: string): DayRange | undefined {
  if (!isDay(first) || !isDay(second) || !isDay(latest)) {
    return undefined;
  }
  const range = first <= second ? { from: first, to: second } : { from: second, to: first };
  return range.to > latest || daysIn(range) > LONGEST_RANGE_DAYS ? undefined : range;
}

/** The same number of days, ending the day before `range` starts. */
export function previousPeriod(range: DayRange): DayRange {
  const start = epochDay(range.from);
  const length = daysIn(range);
  return { from: dayOf(start - length), to: dayOf(start - 1) };
}

/**
 * A figure's change against the period before - QUIETLY (design 18; the owner, 2026-10-05: "Quiet
 * arrow, counts for small numbers"). `text` carries no sign: the arrow `ChangeNote` draws beside it
 * says which way (components/change-note.tsx), and `says` is the whole sentence a screen reader
 * hears - "12% more than the previous period". `undefined` when there is nothing honest to say:
 * either figure unknown, or nothing at all the period before (a rise from nothing has no fair
 * figure, Ghost's and Plausible's rule too).
 *
 * ★ A SMALL BASE IS A COUNT, NOT A PERCENTAGE. When the period before had fewer than
 * `SMALL_BASE` (50) - 11 comments against 17 - the change is "6 fewer", never "-35%": a percentage
 * of a handful reads as an alarm it is not. `asCount: false` (a duration in milliseconds) keeps the
 * percentage whatever the base. Rounded to whole per cent; a change that rounds to 0 is "no change".
 */
export const SMALL_BASE = 50;

export function changeBetween(
  current: number | undefined,
  previous: number | undefined,
  { asCount = true }: { readonly asCount?: boolean } = {},
): StatDelta | undefined {
  if (current === undefined || previous === undefined || previous <= 0) {
    return undefined;
  }
  const t = i18n.getFixedT(null, 'cabinet');
  const flat: StatDelta = { text: t('change.none'), tone: 'flat', says: t('change.noneSays') };
  if (asCount && previous < SMALL_BASE) {
    const difference = Math.round(current - previous);
    if (difference === 0) {
      return flat;
    }
    const size = String(Math.abs(difference));
    return difference > 0
      ? { text: t('change.more', { size }), tone: 'up', says: t('change.moreSays', { size }) }
      : { text: t('change.fewer', { size }), tone: 'down', says: t('change.fewerSays', { size }) };
  }
  const percent = Math.round(((current - previous) / previous) * 100);
  if (percent === 0) {
    return flat;
  }
  const size = String(Math.abs(percent));
  return percent > 0
    ? {
        text: t('change.percent', { size }),
        tone: 'up',
        says: t('change.percentMoreSays', { size }),
      }
    : {
        text: t('change.percent', { size }),
        tone: 'down',
        says: t('change.percentLessSays', { size }),
      };
}
